import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles, ShieldCheck, Building2 } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PLANS, FAQS } from "../lib/content";

const ACCENTS = {
    cyan: { ring: "border-cyan-500/50 shadow-[0_0_45px_rgba(6,182,212,0.15)]", badge: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25", btn: "from-blue-600 to-cyan-500 text-white" },
    blue: { ring: "border-blue-500/40", badge: "bg-blue-500/10 text-blue-300 border-blue-500/25", btn: "from-blue-600 to-indigo-500 text-white" },
    amber: { ring: "border-amber-500/50 shadow-[0_0_50px_rgba(245,158,11,0.14)]", badge: "bg-amber-500/10 text-amber-300 border-amber-500/25", btn: "from-amber-500 to-orange-500 text-white" },
};

const BILLING_POINTS = [
    "No automatic monthly recharge",
    "GST invoice on every payment",
    "Usage allowances configured before activation",
    "Extra usage charged only on actual consumption",
];

const SmallPlanCard = ({ plan, accent }) => {
    const [option, setOption] = useState("A");
    return (
        <div className={`relative flex h-full flex-col rounded-3xl border bg-gradient-to-b from-slate-900/90 to-slate-900/50 p-8 ${accent.ring}`} data-testid={`plan-card-${plan.id}`}>
            <span className="absolute -top-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-1 text-xs font-bold text-white">
                <Sparkles className="h-3 w-3" /> {plan.badge}
            </span>
            <h3 className="font-display text-xl font-bold text-white">{plan.name}</h3>
            <p className="mt-1 text-sm text-slate-500">{plan.tagline}</p>
            <div className="mt-6 flex items-end gap-1.5">
                <span className="font-display text-4xl font-extrabold text-white">{plan.price}</span>
                <span className="pb-1 text-sm text-slate-500">{plan.period}</span>
            </div>
            <p className="mt-1 text-xs text-emerald-400">Pay only for what you use. No mandatory monthly bill.</p>

            <p className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Choose your recharge</p>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="small-plan-options">
                {plan.options.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        onClick={() => setOption(o.id)}
                        data-testid={`small-plan-option-${o.id}`}
                        className={`rounded-xl border px-3 py-2.5 text-center transition-colors ${
                            option === o.id
                                ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200"
                                : "border-white/10 bg-white/5 text-slate-400 hover:border-cyan-500/30"
                        }`}
                    >
                        <span className="block text-[9px] font-bold uppercase tracking-wider">{o.label}</span>
                        <span className="mt-0.5 block text-xs font-semibold">{o.value}</span>
                    </button>
                ))}
            </div>
            <p className="mt-2 text-[10px] text-slate-600">One option per recharge — both are not included together.</p>

            <ul className="mt-5 flex-1 space-y-2.5">
                {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {f}
                    </li>
                ))}
            </ul>
            <p className={`mt-5 rounded-lg border px-3 py-2 text-center text-xs font-semibold ${accent.badge}`}>{plan.note}</p>
            <Link
                to="/contact"
                data-testid={`plan-cta-${plan.id}`}
                className={`mt-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r px-6 py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02] ${accent.btn}`}
            >
                {plan.cta}
            </Link>
        </div>
    );
};

export default function Pricing() {
    return (
        <div className="relative overflow-hidden" data-testid="pricing-page">
            <div className="glow-orb left-[30%] top-[-5%] h-[360px] w-[560px] bg-blue-600/15" />
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
                <Reveal className="text-center">
                    <Eyebrow>Pricing</Eyebrow>
                    <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="pricing-heading">
                        From one AI receptionist to an <span className="cyan-gradient-text">entire AI workforce</span>
                    </h1>
                    <p className="mx-auto mt-4 max-w-2xl text-slate-400">
                        Start your small business at ₹499 — Enterprise-level AI automation at ₹40,000.
                        Big or small, WorkAssign AI grows with you at every level.
                    </p>
                </Reveal>

                <div className="mx-auto mt-16 grid max-w-5xl items-stretch gap-8 lg:grid-cols-2">
                    {PLANS.map((p, i) => {
                        const a = ACCENTS[p.accent];
                        return (
                            <Reveal key={p.id} delay={i * 0.12} className="h-full">
                                {p.id === "small" ? (
                                    <SmallPlanCard plan={p} accent={a} />
                                ) : (
                                    <div className={`relative flex h-full flex-col rounded-3xl border bg-gradient-to-b from-slate-900/90 to-slate-900/50 p-8 ${a.ring}`} data-testid={`plan-card-${p.id}`}>
                                        {p.premium && (
                                            <>
                                                <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
                                                <img
                                                    src="/assets/robot-leadgen.png"
                                                    alt="Enterprise AI agent"
                                                    className="animate-floaty absolute -top-10 right-4 h-20 w-auto object-contain"
                                                    style={{ filter: "drop-shadow(0 12px 18px rgba(2,6,17,0.6))" }}
                                                />
                                            </>
                                        )}
                                        {p.badge && (
                                            <span className={`mb-3 w-fit rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${a.badge}`}>
                                                {p.badge}
                                            </span>
                                        )}
                                        <h3 className="font-display text-xl font-bold text-white">{p.name}</h3>
                                        <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
                                        <div className="mt-6 flex items-end gap-1.5">
                                            <span className={`font-display text-4xl font-extrabold ${p.premium ? "amber-gradient-text" : "text-white"}`}>{p.price}</span>
                                            <span className="pb-1 text-sm text-slate-500">{p.period}</span>
                                        </div>
                                        {p.bestFor && (
                                            <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-slate-500">
                                                <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" /> {p.bestFor}
                                            </p>
                                        )}
                                        <ul className="mt-5 flex-1 space-y-2.5">
                                            {p.features.map((f) => (
                                                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                                                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.premium ? "text-amber-400" : "text-blue-400"}`} /> {f}
                                                </li>
                                            ))}
                                        </ul>
                                        <p className={`mt-5 rounded-lg border px-3 py-2 text-center text-xs font-semibold ${a.badge}`}>{p.note}</p>
                                        <Link
                                            to="/contact"
                                            data-testid={`plan-cta-${p.id}`}
                                            className={`mt-6 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r px-6 py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02] ${a.btn}`}
                                        >
                                            {p.cta}
                                        </Link>
                                        {p.premium && (
                                            <Link
                                                to="/contact"
                                                data-testid="plan-cta-enterprise-demo"
                                                className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-6 py-3 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/20"
                                            >
                                                Book Enterprise Demo
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </Reveal>
                        );
                    })}
                </div>

                <Reveal delay={0.2}>
                    <div className="mx-auto mt-12 flex max-w-4xl flex-wrap items-center justify-center gap-3" data-testid="billing-transparency">
                        {BILLING_POINTS.map((b) => (
                            <span key={b} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-300">
                                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> {b}
                            </span>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.2} className="mt-16">
                    <div className="mx-auto max-w-3xl">
                        <h2 className="text-center font-display text-2xl font-bold text-white sm:text-3xl" data-testid="faq-heading">
                            Frequently asked <span className="cyan-gradient-text">questions</span>
                        </h2>
                        <Accordion type="single" collapsible className="mt-8 space-y-3" data-testid="faq-accordion">
                            {FAQS.map((f, i) => (
                                <AccordionItem key={i} value={`faq-${i}`} className="glow-card rounded-xl border-white/10 px-5" data-testid={`faq-item-${i}`}>
                                    <AccordionTrigger className="text-left font-display text-sm font-semibold text-white hover:text-cyan-300 hover:no-underline">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
